import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { sriLankaBoundary, sriLankaDistricts } from '../../utils/sriLankaGeoJSON';
import api from '../../services/api';

// Extend Leaflet types
declare module 'leaflet' {
  function heatLayer(
    latlngs: [number, number, number][],
    options?: any
  ): L.Layer;
}

const HeatMapLayer: React.FC<{ points: [number, number, number][] }> = ({ points }) => {
  const map = useMap();

  useEffect(() => {
    if (!points.length) return;

    const heat = (L as any).heatLayer(points, {
      radius: 35,
      blur: 45,
      maxZoom: 10,
      max: 1.0,
      gradient: {
        0.0: '#00ff00',
        0.4: '#ffff00',
        0.6: '#ff9900',
        0.8: '#ff0000',
        1.0: '#990000',
      },
    }).addTo(map);

    return () => {
      map.removeLayer(heat);
    };
  }, [map, points]);

  return null;
};

const SriLankaHeatMap: React.FC = () => {
  const { t } = useTranslation();
  const [heatData, setHeatData] = useState<[number, number, number][]>([]);
  const [postCount, setPostCount] = useState(0);

  useEffect(() => {
    const fetchHeatData = async () => {
      try {
        // Using mock data as requested to avoid CORS and dependency on external API
        const mockPosts = [
          { latitude: 6.9271, longitude: 79.8612 }, // Colombo
          { latitude: 7.2906, longitude: 80.6337 }, // Kandy
          { latitude: 6.0535, longitude: 80.2210 }, // Galle
          { latitude: 9.6615, longitude: 80.0255 }, // Jaffna
          { latitude: 8.5874, longitude: 81.2152 }, // Trincomalee
          { latitude: 7.9403, longitude: 81.0188 }, // Polonnaruwa
          { latitude: 6.9934, longitude: 81.0550 }, // Badulla
          { latitude: 6.7056, longitude: 80.3847 }, // Ratnapura
          { latitude: 7.2008, longitude: 79.8737 }, // Negombo
          { latitude: 8.3114, longitude: 80.4037 }, // Anuradhapura
        ];

        setPostCount(mockPosts.length);
        const points: [number, number, number][] = mockPosts
          .map((post: any) => [
            post.latitude,
            post.longitude,
            0.8 // Intensity for each report
          ]);
        setHeatData(points);
      } catch (error) {
        console.error('Error setting heat map data:', error);
      }
    };

    fetchHeatData();
  }, []);

  return (
    <div className="relative h-full w-full">
      <MapContainer
        center={[7.8731, 80.7718]}
        zoom={8}
        style={{ height: '100%', width: '100%' }}
        className="rounded-lg shadow-xl"
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        />

        <GeoJSON
          data={sriLankaBoundary as any}
          style={{
            color: '#2563eb',
            weight: 2,
            fillOpacity: 0,
          }}
        />

        <HeatMapLayer points={heatData} />
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white p-4 rounded-lg shadow-lg z-[1000]">
        <h3 className="font-bold mb-3 text-gray-800">{t('map.legend', 'Risk Levels')}</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded" style={{ background: '#00ff00' }}></div>
            <span className="text-sm">{t('map.lowRisk', 'Low Risk')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded" style={{ background: '#ffff00' }}></div>
            <span className="text-sm">{t('map.mediumRisk', 'Medium Risk')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded" style={{ background: '#ff9900' }}></div>
            <span className="text-sm">{t('map.highRisk', 'High Risk')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded" style={{ background: '#ff0000' }}></div>
            <span className="text-sm">{t('map.veryHighRisk', 'Critical')}</span>
          </div>
        </div>
      </div>

      {/* Affected Areas Stats */}
      <div className="absolute top-4 left-4 bg-white p-4 rounded-lg shadow-lg z-[1000] max-w-xs">
        <h3 className="font-bold mb-2 text-gray-800">{t('map.affectedAreas', 'Active Reports')}</h3>
        <div className="text-2xl font-bold text-red-600 mb-1">{postCount}</div>
        <p className="text-sm text-gray-600">Total Incidents Reported</p>
      </div>
    </div>
  );
};

export default SriLankaHeatMap;
